import type { CardData, InstallmentOption, PaymentToken } from '../types/cart'
import EfiPay from 'payment-token-efi'

interface PaymentTokenResponse {
  payment_token: string
  card_mask: string
}

interface InstallmentsResponse {
  installments: Array<{
    installment: number
    has_interest: boolean
    value: number
    currency: string
  }>
}

export class PaymentService {
  private payeeCode: string
  private environment: 'production' | 'sandbox'

  constructor() {
    this.payeeCode = (import.meta as any)?.env?.VITE_EFI_PAYEE_CODE || ''
    this.environment = 'production'
  }

  isScriptLoaded(): boolean {
    return typeof EfiPay !== 'undefined'
  }

  async identifyCardBrand(cardNumber: string): Promise<string> {
    const cleanNumber = cardNumber.replace(/\D/g, '')
    try {
      if (cleanNumber.length >= 6) {
        // @ts-ignore
        const brand = await EfiPay.CreditCard.setCardNumber(cleanNumber).verifyCardBrand()
        return brand
      }
      return 'unknown'
    } catch {
      return 'unknown'
    }
  }

  async generatePaymentToken(cardData: CardData): Promise<PaymentToken> {
    if (!this.payeeCode) throw new Error('Código do payee não configurado')

    // @ts-ignore
    const result = (await EfiPay.CreditCard
      .setAccount(this.payeeCode)
      .setEnvironment(this.environment)
      .setCreditCardData({
        brand: cardData.brand,
        number: cardData.number.replace(/\D/g, ''),
        cvv: cardData.cvv,
        expirationMonth: cardData.expirationMonth,
        expirationYear: cardData.expirationYear,
        holderName: cardData.holderName,
        holderDocument: cardData.holderDocument || '',
        reuse: false
      })
      .getPaymentToken()) as PaymentTokenResponse

    return { payment_token: result.payment_token, card_mask: result.card_mask }
  }

  async getInstallmentOptions(amount: number, brand: string): Promise<InstallmentOption[]> {
    if (!this.payeeCode) throw new Error('Código do payee não configurado')
    // @ts-ignore
    const result = (await EfiPay.CreditCard
      .setAccount(this.payeeCode)
      .setEnvironment(this.environment)
      .setBrand(brand)
      .setTotal(Math.round(amount * 100))
      .getInstallments()) as InstallmentsResponse

    return result.installments.map((inst) => ({
      installment: inst.installment,
      value: inst.value / 100,
      currency: inst.currency,
      has_interest: inst.has_interest
    }))
  }

  formatCardNumber(value: string): string {
    return value.replace(/\D/g, '').slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ')
  }

  formatCVV(value: string): string {
    return value.replace(/\D/g, '').slice(0, 4)
  }

  validateCardData(cardData: CardData): { isValid: boolean; errors: string[] } {
    const errors: string[] = []
    const cleanNumber = cardData.number.replace(/\D/g, '')
    if (cleanNumber.length < 13 || cleanNumber.length > 19) errors.push('Número do cartão deve ter entre 13 e 19 dígitos')
    if (cardData.cvv.length < 3 || cardData.cvv.length > 4) errors.push('CVV deve ter 3 ou 4 dígitos')
    const month = parseInt(cardData.expirationMonth, 10)
    if (Number.isNaN(month) || month < 1 || month > 12) errors.push('Mês de expiração inválido')
    const year = parseInt(cardData.expirationYear, 10)
    if (Number.isNaN(year) || cardData.expirationYear.length < 2) errors.push('Ano de expiração inválido')
    if (!cardData.holderName.trim()) errors.push('Nome do portador é obrigatório')
    if (!cardData.brand || cardData.brand === 'unknown') errors.push('Bandeira do cartão inválida')
    return { isValid: errors.length === 0, errors }
  }
}

export const paymentService = new PaymentService()

